import { useCallback } from 'react';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

export interface UseNoInternetReturn {
  isOnline: boolean;
  handleRetry: () => void;
}

/**
 * Custom hook for NoInternet component
 * Handles network status monitoring and retry logic
 */
export const useNoInternet = (): UseNoInternetReturn => {
  const isOnline = useNetworkStatus();

  const handleRetry = useCallback(async () => {
    // Check network connectivity without reloading the page
    // Try to fetch a lightweight resource to verify actual internet connection
    try {
      // Use a small, fast endpoint or a known resource
      // Using a cache-busting parameter to ensure we make a real network request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      await fetch(window.location.origin, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // If fetch succeeds, manually trigger online event to update network status
      // This will cause useNetworkStatus to re-evaluate and update the UI
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new Event('online'));
      }
    } catch (_error) {
      // Connection check failed - stay in no-internet UI
      // The isOnline state will remain false, so the UI won't change
      // No action needed - user stays on the no-internet screen
    }
  }, []);

  return {
    isOnline,
    handleRetry,
  };
};
